import { useCallback, useEffect, useMemo, useState } from 'react';
import { useUrlParam, useUrlPanelSync } from '../../../../hooks/useUrlParam';
import { useUser } from '../../../../context/UserContext';
import { AI_JOB_STATUS_LABEL, aiJobStatusTone } from '../../../../lib/aiJobLabels';
import {
  checkpoints,
  enqueueDensityFix,
  enqueueLinksFix,
  enqueueTemplateFix,
  enqueueRetroOpt,
  fetchPageOptimizations,
  logIndexRequest,
  OPTIMIZATION_KIND_LABEL,
  splitSpec,
  type OveroptTarget,
  type PageOptimization,
  type RetroOptBaseline,
  type WindowStats,
} from '../../../../lib/retroOpt';
import AiMarkdown from '../../../ai/AiMarkdown';
import ModelPicker from '../../../ai/ModelPicker';
import { Badge, Button, Card, Checkbox, Input, SidePanel, Table, Tabs, useToast, type TableColumn } from '../../../ui';
import OveroptBadge from './OveroptBadge';
import { fmtDec, fmtInt, LINK_FLOOR, overoptOf, templateOf, type PageHistoryRow, type PageQueryRow, type PageRow } from './format';

const faint: React.CSSProperties = { color: 'var(--text-faint)' };
const flatTable: React.CSSProperties = { background: 'transparent', border: 'none', borderRadius: 0, boxShadow: 'none', minWidth: '620px' };
const DOC_TABS = ['Analysis', 'Plan', 'Execution', 'Verify'] as const;
const textareaStyle: React.CSSProperties = {
  width: '100%',
  resize: 'vertical',
  minHeight: '88px',
  padding: '10px 12px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border-hairline)',
  background: 'var(--surface-input)',
  color: 'var(--text-body)',
  font: 'inherit',
};
type DocTab = (typeof DOC_TABS)[number];

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

/** "pos 8.8 · 117 impr/wk", or a dash until the window is fully measured. */
const stat = (s: WindowStats | null) =>
  s ? (
    <span className="t-num">
      pos {fmtDec(s.position)} <span style={faint}>· {fmtInt(s.impressions)} impr/wk</span>
    </span>
  ) : (
    <span style={faint}>—</span>
  );

interface RetroOptJournalProps {
  client: { id: string; name: string };
  page: PageRow;
  history: PageHistoryRow[];
  queries: PageQueryRow[];
}

/**
 * Page detail → the journal: buttons that send this page back through the agent queue (Retroactive
 * Optimization, a Keyword Density Fix for phrases said too often or too rarely, or a Templated Content Fix for
 * a page that copies a sibling), and every time it went. "Requested indexing" queues nothing: it records that
 * someone asked Google to recrawl the page in Search Console, which keeps no history of its own.
 * Each row opens the job's docs (Analysis and Plan from the spec, Execution from the build report, Verify
 * from the approval) with before / +28 / +56 day numbers from the page's weekly history.
 */
export default function RetroOptJournal({ client, page, history, queries }: RetroOptJournalProps) {
  const { user } = useUser();
  const toast = useToast();
  const [entries, setEntries] = useState<PageOptimization[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [queueOpen, setQueueOpen] = useState(false);
  const [phrase, setPhrase] = useState('');
  const [note, setNote] = useState('');
  const [isQueueing, setIsQueueing] = useState(false);
  const [isLogging, setIsLogging] = useState(false);
  const [fixOpen, setFixOpen] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [templateNote, setTemplateNote] = useState('');
  const [templateModel, setTemplateModel] = useState<string | null>(null);
  const [linksOpen, setLinksOpen] = useState(false);
  const [linksNote, setLinksNote] = useState('');
  const [linksModel, setLinksModel] = useState<string | null>(null);
  const [fixPicked, setFixPicked] = useState<Set<string>>(new Set());
  const [fixExtra, setFixExtra] = useState<string[]>([]);
  const [fixTyped, setFixTyped] = useState('');
  const [fixNote, setFixNote] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  // The open journal entry is ?entry=<id>, so back closes it and a refresh reopens it.
  const [urlEntry, setUrlEntry] = useUrlParam('entry');
  const [tab, setTab] = useState<DocTab>('Analysis');

  const load = useCallback(async () => {
    try {
      setError(null);
      setEntries(await fetchPageOptimizations(page.id));
    } catch (err) {
      console.error('Error loading retro opt journal:', err);
      setError('Couldn’t load this page’s journal');
    } finally {
      setIsLoading(false);
    }
  }, [page.id]);

  useEffect(() => {
    setIsLoading(true);
    void load();
  }, [load]);

  // The phrase the page earned the most impressions on, as the default target.
  const suggested = useMemo(() => queries[0]?.query ?? page.ahrefs_top_keyword ?? '', [queries, page.ahrefs_top_keyword]);
  const openQueue = () => {
    setPhrase(suggested);
    setNote('');
    setQueueOpen(true);
  };

  const queue = async () => {
    if (!user) return;
    setIsQueueing(true);
    try {
      await enqueueRetroOpt({ client, page, primaryPhrase: phrase, note, createdBy: { id: user.id, name: user.name || user.email } });
      toast.success('Queued', 'The analysis and plan land in the journal when the spec is ready.');
      setQueueOpen(false);
      await load();
    } catch (err) {
      toast.error('Couldn’t queue the retro opt', err instanceof Error ? err.message : 'Nothing was queued.');
    } finally {
      setIsQueueing(false);
    }
  };

  // The screen's phrases, flagged ones first and ticked; a phrase can be typed in when the screen missed it.
  const screened = useMemo(() => overoptOf(page)?.phrases ?? [], [page]);
  const openFix = () => {
    setFixPicked(new Set(screened.filter((p) => p.level).map((p) => p.phrase)));
    setFixExtra([]);
    setFixTyped('');
    setFixNote('');
    setFixOpen(true);
  };
  const addTyped = () => {
    const phrase = fixTyped.trim().toLowerCase();
    if (!phrase) return;
    if (!screened.some((p) => p.phrase === phrase) && !fixExtra.includes(phrase)) setFixExtra([...fixExtra, phrase]);
    setFixPicked(new Set([...fixPicked, phrase]));
    setFixTyped('');
  };
  const togglePick = (phrase: string, on: boolean) => {
    const next = new Set(fixPicked);
    if (on) next.add(phrase);
    else next.delete(phrase);
    setFixPicked(next);
  };
  const fixTargets: OveroptTarget[] = [
    ...screened.filter((p) => fixPicked.has(p.phrase)),
    ...fixExtra.filter((p) => fixPicked.has(p)).map((phrase) => ({ phrase })),
  ];

  const queueFix = async () => {
    if (!user) return;
    setIsQueueing(true);
    try {
      await enqueueDensityFix({ client, page, phrases: fixTargets, note: fixNote, createdBy: { id: user.id, name: user.name || user.email } });
      toast.success('Queued', 'The analysis and plan land in the journal when the spec is ready.');
      setFixOpen(false);
      await load();
    } catch (err) {
      toast.error('Couldn’t queue the fix', err instanceof Error ? err.message : 'Nothing was queued.');
    } finally {
      setIsQueueing(false);
    }
  };

  const template = templateOf(page);
  const queueTemplateFix = async () => {
    if (!user) return;
    setIsQueueing(true);
    try {
      await enqueueTemplateFix({ client, page, note: templateNote, model: templateModel, createdBy: { id: user.id, name: user.name || user.email } });
      toast.success('Queued', 'The analysis and plan land in the journal when the spec is ready.');
      setTemplateOpen(false);
      await load();
    } catch (err) {
      toast.error('Couldn’t queue the fix', err instanceof Error ? err.message : 'Nothing was queued.');
    } finally {
      setIsQueueing(false);
    }
  };

  const queueLinksFix = async () => {
    if (!user) return;
    setIsQueueing(true);
    try {
      await enqueueLinksFix({ client, page, note: linksNote, model: linksModel, createdBy: { id: user.id, name: user.name || user.email } });
      toast.success('Queued', 'The analysis and plan land in the journal when the spec is ready.');
      setLinksOpen(false);
      await load();
    } catch (err) {
      toast.error('Couldn’t queue the fix', err instanceof Error ? err.message : 'Nothing was queued.');
    } finally {
      setIsQueueing(false);
    }
  };

  // Entries come newest first, so this is the last time anyone asked Google to recrawl the page.
  const lastIndexRequest = entries.find((e) => e.kind === 'index_request') ?? null;
  const requestIndexing = async () => {
    if (!user) return;
    setIsLogging(true);
    try {
      await logIndexRequest({ client, page, createdBy: { id: user.id } });
      toast.success('Logged', 'The +28 and +56 day numbers in the journal measure from today.');
      await load();
    } catch (err) {
      toast.error('Couldn’t log the request', err instanceof Error ? err.message : 'Nothing was saved.');
    } finally {
      setIsLogging(false);
    }
  };

  useUrlPanelSync(
    urlEntry,
    openId,
    !isLoading,
    (id) => {
      setTab('Analysis');
      setOpenId(id);
    },
    () => setOpenId(null),
  );

  const open = entries.find((e) => e.id === openId) ?? null;
  const openDocs = useMemo(() => {
    if (!open?.job) return null;
    const { analysis, plan } = splitSpec(open.job.spec_body);
    return { Analysis: analysis, Plan: plan, Execution: open.job.report_body, Verify: open.job.approval_body } as Record<DocTab, string>;
  }, [open]);
  const openPoints = useMemo(() => checkpoints(history, open?.shipped_at ?? null), [history, open?.shipped_at]);

  const columns: TableColumn<PageOptimization>[] = [
    { key: 'created_at', label: 'Queued', render: (v) => fmtDate(v as string) },
    {
      key: 'kind',
      label: 'Job',
      render: (v) =>
        v === 'density_fix' ? 'Density fix' : v === 'template_fix' ? 'Template fix' : v === 'links_fix' ? 'Links fix' : v === 'index_request' ? 'Indexing requested' : 'Retro opt',
    },
    {
      key: 'job',
      label: 'Status',
      render: (_, row) =>
        row.kind === 'index_request' ? (
          <Badge tone="neutral">In Search Console</Badge>
        ) : row.job ? (
          <Badge tone={aiJobStatusTone(row.job.status)}>{AI_JOB_STATUS_LABEL[row.job.status]}</Badge>
        ) : (
          <span style={faint}>Job removed</span>
        ),
    },
    {
      key: 'baseline',
      label: 'When queued',
      render: (v) => {
        const b = v as RetroOptBaseline | null;
        return b ? (
          <span className="t-num">
            pos {fmtDec(b.position)} <span style={faint}>· {fmtInt(b.impressions)} impr</span>
          </span>
        ) : (
          '—'
        );
      },
    },
    { key: 'shipped_at', label: 'Shipped', render: (v) => fmtDate(v as string | null) },
    { key: 'd28', label: '+28 days', render: (_, row) => stat(checkpoints(history, row.shipped_at).d28) },
    { key: 'd56', label: '+56 days', render: (_, row) => stat(checkpoints(history, row.shipped_at).d56) },
  ];

  const empty = (text: string) => <div style={{ padding: '24px 0', textAlign: 'center', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>{text}</div>;

  return (
    <>
      <Card
        eyebrow="Optimization"
        title="Journal"
        actions={
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center' }}>
            {lastIndexRequest ? (
              <span style={{ ...faint, fontSize: 'var(--text-xs)' }}>Indexing last requested {fmtDate(lastIndexRequest.created_at)}</span>
            ) : null}
            <Button
              variant="secondary"
              size="sm"
              icon="search"
              onClick={requestIndexing}
              disabled={isLogging || !user}
              title="Log that you clicked Request indexing for this page in Search Console. Google keeps no history of it."
            >
              {isLogging ? 'Logging...' : 'Requested indexing'}
            </Button>
            <Button variant="secondary" size="sm" icon="link" onClick={() => { setLinksNote(''); setLinksModel(null); setLinksOpen(true); }}>
              Internal Links Fix
            </Button>
            <Button variant="secondary" size="sm" icon="layers" onClick={() => { setTemplateNote(''); setTemplateModel(null); setTemplateOpen(true); }}>
              Templated Content Fix
            </Button>
            <Button variant="secondary" size="sm" icon="percent" onClick={openFix}>
              Keyword Density Fix
            </Button>
            <Button variant="primary" size="sm" icon="refresh-cw" onClick={openQueue}>
              Retroactive Optimization
            </Button>
          </div>
        }
      >
        {error ? <div style={{ color: 'var(--danger-400)', fontSize: 'var(--text-sm)' }}>{error}</div> : null}
        {isLoading ? (
          empty('Loading...')
        ) : entries.length === 0 ? (
          empty('This page hasn’t been through a retro opt, a keyword density fix, a templated content fix or an internal links fix yet, and nobody has logged an indexing request.')
        ) : (
          <div style={{ overflowX: 'auto', margin: '0 calc(-1 * var(--space-2))' }}>
            <Table
              columns={columns}
              rows={entries}
              dense
              style={flatTable}
              onRowClick={(row) => setUrlEntry(row.id)}
            />
          </div>
        )}
      </Card>

      <SidePanel
        open={queueOpen}
        onClose={() => setQueueOpen(false)}
        eyebrow="Retroactive Optimization"
        title="Send this page back through the queue"
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <Button variant="ghost" onClick={() => setQueueOpen(false)} disabled={isQueueing}>
              Cancel
            </Button>
            <Button variant="primary" onClick={queue} disabled={isQueueing || !user}>
              {isQueueing ? 'Queueing...' : 'Queue it'}
            </Button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', fontSize: 'var(--text-sm)' }}>
          <p style={{ margin: 0, color: 'var(--text-muted)' }}>
            The agent audits <span style={{ overflowWrap: 'anywhere' }}>{page.url}</span>, spam-penalty risk first, and stops with an analysis and a plan
            for you to approve. Nothing on the page changes until you do.
          </p>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span className="t-eyebrow">Primary phrase</span>
            <Input value={phrase} onChange={(e) => setPhrase(e.target.value)} placeholder="The search the page should win" />
            <span style={{ ...faint, fontSize: 'var(--text-xs)' }}>Defaults to the query this page earned the most impressions on.</span>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span className="t-eyebrow">Why now</span>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional. e.g. dropped in the September spam update."
              rows={4}
              disabled={isQueueing}
              style={textareaStyle}
            />
          </label>
        </div>
      </SidePanel>

      <SidePanel
        open={linksOpen}
        onClose={() => setLinksOpen(false)}
        eyebrow="Internal Links Fix"
        title="Get links pointing at this page"
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <Button variant="ghost" onClick={() => setLinksOpen(false)} disabled={isQueueing}>
              Cancel
            </Button>
            <Button variant="primary" onClick={queueLinksFix} disabled={isQueueing || !user}>
              {isQueueing ? 'Queueing...' : 'Queue it'}
            </Button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', fontSize: 'var(--text-sm)' }}>
          <p style={{ margin: 0, color: 'var(--text-muted)' }}>
            {page.inlinks == null ? 'The crawl hasn’t counted the links pointing at this page yet.' : `${fmtInt(page.inlinks)} internal link${page.inlinks === 1 ? ' points' : 's point'} at this page.`}{' '}
            The agent adds one link each from relevant pages that have room under their link cap, until at least {LINK_FLOOR} point here,
            then stops with an analysis and a plan for you to approve. Nav, footer and this page’s own design stay untouched.
          </p>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span className="t-eyebrow">Why now</span>
            <textarea
              value={linksNote}
              onChange={(e) => setLinksNote(e.target.value)}
              placeholder="Optional. e.g. rebuilt last week and Google still hasn’t indexed it."
              rows={4}
              disabled={isQueueing}
              style={textareaStyle}
            />
          </label>
          <ModelPicker
            variant="field"
            value={linksModel}
            onChange={setLinksModel}
            disabled={isQueueing}
            hint="Which LLM runs this job, spec and build. Default is the agent's own model, currently Opus 5.5."
          />
        </div>
      </SidePanel>

      <SidePanel
        open={templateOpen}
        onClose={() => setTemplateOpen(false)}
        eyebrow="Templated Content Fix"
        title="Make this page its own"
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <Button variant="ghost" onClick={() => setTemplateOpen(false)} disabled={isQueueing}>
              Cancel
            </Button>
            <Button variant="primary" onClick={queueTemplateFix} disabled={isQueueing || !user}>
              {isQueueing ? 'Queueing...' : 'Queue it'}
            </Button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', fontSize: 'var(--text-sm)' }}>
          <p style={{ margin: 0, color: 'var(--text-muted)' }}>
            The agent rewrites <span style={{ overflowWrap: 'anywhere' }}>{page.url}</span> from its own search intent and its own proof, until it shares under
            20% with every sibling, then stops with an analysis and a plan for you to approve. Nothing on the page changes until you do.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span className="t-eyebrow">Shares its text with</span>
            {template?.siblings.length ? (
              template.siblings.map((s) => (
                <span key={s.url} className="t-num" style={{ fontSize: 'var(--text-xs)', overflowWrap: 'anywhere' }}>
                  {Math.round(s.share * 100)}% <span style={faint}>· {s.url}</span>
                </span>
              ))
            ) : (
              <span style={faint}>The check found no sibling over 20%. The agent re-measures before planning.</span>
            )}
          </div>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span className="t-eyebrow">Why now</span>
            <textarea
              value={templateNote}
              onChange={(e) => setTemplateNote(e.target.value)}
              placeholder="Optional. e.g. Google indexed Abbotsford but not this one."
              rows={4}
              disabled={isQueueing}
              style={textareaStyle}
            />
          </label>
          <ModelPicker
            variant="field"
            value={templateModel}
            onChange={setTemplateModel}
            disabled={isQueueing}
            hint="Which LLM runs this job, spec and build. Default is the agent's own model, currently Opus 5.5. Changeable from the queue card until an agent picks it up."
          />
        </div>
      </SidePanel>

      <SidePanel
        open={fixOpen}
        onClose={() => setFixOpen(false)}
        eyebrow="Keyword Density Fix"
        title="Bring density into line"
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <Button variant="ghost" onClick={() => setFixOpen(false)} disabled={isQueueing}>
              Cancel
            </Button>
            <Button variant="primary" onClick={queueFix} disabled={isQueueing || !user || fixTargets.length === 0}>
              {isQueueing ? 'Queueing...' : fixTargets.length > 1 ? `Queue it · ${fixTargets.length} phrases` : 'Queue it'}
            </Button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', fontSize: 'var(--text-sm)' }}>
          <p style={{ margin: 0, color: 'var(--text-muted)' }}>
            The agent re-measures each phrase on <span style={{ overflowWrap: 'anywhere' }}>{page.url}</span>, decides whether it is over 1% or under 0.5%,
            then stops with an analysis and a plan to bring it into line for you to approve. Nothing on the page changes until you do.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <span className="t-eyebrow">Phrases</span>
            {screened.length === 0 && fixExtra.length === 0 ? (
              <span style={faint}>The check hasn’t found anything on this page. Type the search in below.</span>
            ) : null}
            {screened.map((p) => (
              <Checkbox
                key={p.phrase}
                checked={fixPicked.has(p.phrase)}
                onChange={(on) => togglePick(p.phrase, on)}
                label={
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    {p.phrase}
                    {p.target ? <span style={{ color: 'var(--accent-text)', fontSize: 'var(--text-2xs)' }}>TARGET</span> : null}
                    <span className="t-num" style={{ ...faint, fontSize: 'var(--text-xs)' }}>
                      {p.uses} use{p.uses === 1 ? '' : 's'} · {fmtDec(p.density, 2)}% · {p.headings} headings
                    </span>
                    <OveroptBadge level={p.level} />
                  </span>
                }
              />
            ))}
            {fixExtra.map((phrase) => (
              <Checkbox key={phrase} checked={fixPicked.has(phrase)} onChange={(on) => togglePick(phrase, on)} label={phrase} />
            ))}
            <div style={{ display: 'flex', gap: '8px' }}>
              <div style={{ flex: 1 }}>
                <Input
                  value={fixTyped}
                  onChange={(e) => setFixTyped(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') addTyped();
                  }}
                  placeholder="Another search, e.g. ceramic coating chilliwack"
                />
              </div>
              <Button variant="secondary" icon="plus" onClick={addTyped} disabled={!fixTyped.trim()}>
                Add
              </Button>
            </div>
          </div>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span className="t-eyebrow">Why now</span>
            <textarea
              value={fixNote}
              onChange={(e) => setFixNote(e.target.value)}
              placeholder="Optional. e.g. every city page has the city in every H2."
              rows={4}
              disabled={isQueueing}
              style={textareaStyle}
            />
          </label>
        </div>
      </SidePanel>

      <SidePanel
        open={!!open}
        onClose={() => setUrlEntry(null)}
        width={820}
        eyebrow={`${OPTIMIZATION_KIND_LABEL[open?.kind ?? 'retro_opt'] ?? 'Optimization'} · ${open?.kind === 'index_request' ? 'logged' : 'queued'} ${fmtDate(open?.created_at ?? null)}`}
        title={open?.primary_phrase ? `“${open.primary_phrase}”` : 'Journal entry'}
      >
        {open ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            {open.note ? <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>{open.note}</div> : null}
            {open.kind === 'template_fix' && Array.isArray(open.targets) && open.targets.length ? (
              <div style={{ fontSize: 'var(--text-sm)' }}>
                <div className="t-eyebrow" style={{ marginBottom: '6px' }}>Siblings, when queued</div>
                {(open.targets as unknown as { url: string; share: number }[]).map((t) => (
                  <div key={t.url} className="t-num" style={{ overflowWrap: 'anywhere' }}>
                    {Math.round(t.share * 100)}% <span style={faint}>· {t.url}</span>
                  </div>
                ))}
              </div>
            ) : null}
            {open.kind !== 'template_fix' && Array.isArray(open.targets) && open.targets.length ? (
              <div style={{ fontSize: 'var(--text-sm)' }}>
                <div className="t-eyebrow" style={{ marginBottom: '6px' }}>Phrases, when queued</div>
                {(open.targets as unknown as OveroptTarget[]).map((t) => (
                  <div key={t.phrase} className="t-num">
                    {t.phrase}
                    {t.uses != null ? (
                      <span style={faint}>
                        {' '}
                        · {t.uses} use{t.uses === 1 ? '' : 's'} · {fmtDec(t.density, 2)}% · {t.headings} headings
                      </span>
                    ) : (
                      <span style={faint}> · typed in</span>
                    )}
                  </div>
                ))}
              </div>
            ) : null}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 'var(--space-3)', fontSize: 'var(--text-sm)' }}>
              {(
                [
                  [open.kind === 'index_request' ? '4 weeks before' : '4 weeks before ship', openPoints.before],
                  ['+28 days', openPoints.d28],
                  ['+56 days', openPoints.d56],
                ] as const
              ).map(([label, s]) => (
                <div key={label} style={{ border: '1px solid var(--border-hairline)', borderRadius: 'var(--radius-sm)', padding: '10px 12px' }}>
                  <div className="t-eyebrow" style={{ marginBottom: '4px' }}>{label}</div>
                  {open.shipped_at ? stat(s) : <span style={faint}>Not shipped yet</span>}
                </div>
              ))}
            </div>
            {open.kind === 'index_request' ? (
              <span style={{ ...faint, fontSize: 'var(--text-sm)' }}>Someone clicked Request indexing for this page in Search Console. No agent job behind it.</span>
            ) : (
              <>
                <Tabs tabs={[...DOC_TABS]} active={tab} onChange={(t) => setTab(t as DocTab)} />
                {openDocs ? <AiMarkdown content={openDocs[tab] ?? ''} /> : <span style={faint}>The job behind this entry was removed.</span>}
              </>
            )}
          </div>
        ) : null}
      </SidePanel>
    </>
  );
}
